// src/components/examples/VideoUploadExample.js
import React, { useState } from 'react';
import VideoUpload from '../common/VideoUpload.js';

const VideoUploadExample = () => {
  const [uploadedVideos, setUploadedVideos] = useState([]);

  // Add debugging to see if component loads
  console.log('VideoUploadExample component loaded!');

  const handleVideoUpload = (videoData) => {
    setUploadedVideos(prev => [...prev, videoData]);
    console.log('Video uploaded:', videoData);
  };

  const handleVideoRemove = (videoId) => {
    setUploadedVideos(prev => prev.filter(v => v.id !== videoId));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h1 className="text-3xl font-bold text-blue-900 mb-4">🎥 Cloudflare Stream Video Upload</h1>
        <p className="text-blue-700 mb-4">
          Upload your exercise videos to Cloudflare Stream. Videos will be automatically set to private and can only be embedded on your domains.
        </p>
        <div className="bg-blue-100 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Features:</h3>
          <ul className="text-blue-800 text-sm space-y-1">
            <li>✅ Private videos by default</li>
            <li>✅ Domain restrictions</li>
            <li>✅ Tier-based access control</li>
            <li>✅ No downloads allowed</li>
            <li>✅ Global CDN delivery</li>
          </ul>
        </div>
      </div>

      {/* Video Upload Component */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Upload New Video</h2>
        <VideoUpload
          onVideoUpload={handleVideoUpload}
          onVideoRemove={() => {}}
          maxSize={100}
        />
      </div>

      {/* Uploaded Videos List */}
      {uploadedVideos.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Uploaded Videos</h2>
          <div className="space-y-4">
            {uploadedVideos.map((video) => (
              <div key={video.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{video.name}</h3>
                    <div className="text-sm text-gray-500 space-y-1 mt-1">
                      <p>ID: <code className="bg-gray-100 px-1 rounded">{video.id}</code></p>
                      <p>Tier: <span className="capitalize">{video.tier}</span></p>
                      <p>Status: <span className="capitalize">{video.status}</span></p>
                      {video.duration && <p>Duration: {Math.round(video.duration)}s</p>}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleVideoRemove(video.id)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                
                {/* Video Preview */}
                {video.preview && (
                  <div className="mt-3">
                    <img 
                      src={video.preview} 
                      alt={video.name}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                )}

                {/* Usage Instructions */}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>To use this video in your app:</strong>
                  </p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>1. Copy the Video ID: <code className="bg-gray-200 px-1 rounded">{video.id}</code></p>
                    <p>2. Update your exercise data:</p>
                    <pre className="bg-gray-200 p-2 rounded text-xs overflow-x-auto">
{`{
  id: 'exercise-name',
  name: 'Exercise Name',
  videoId: '${video.id}',
  platform: 'cloudflare',
  tier: '${video.tier}',
  // ... other data
}`}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Environment Check */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Environment Check</h2>
        <div className="space-y-2 text-sm">
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>Account ID: {process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID ? '✅ Set' : '❌ Not set'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${process.env.REACT_APP_CLOUDFLARE_API_TOKEN ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span>API Token: {process.env.REACT_APP_CLOUDFLARE_API_TOKEN ? '✅ Set' : '❌ Not set'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${process.env.REACT_APP_CLOUDFLARE_STREAM_DOMAIN ? 'bg-green-500' : 'bg-green-500'}`}></span>
            <span>Stream Domain: {process.env.REACT_APP_CLOUDFLARE_STREAM_DOMAIN || 'videodelivery.net (default)'}</span>
          </div>
        </div>
        
        {(!process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID || !process.env.REACT_APP_CLOUDFLARE_API_TOKEN) && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-yellow-800 text-sm">
              ⚠️ Please set your Cloudflare environment variables in Vercel:
            </p>
            <code className="block mt-2 text-xs bg-yellow-100 p-2 rounded">
              REACT_APP_CLOUDFLARE_ACCOUNT_ID=your_account_id<br/>
              REACT_APP_CLOUDFLARE_API_TOKEN=your_api_token<br/>
              REACT_APP_CLOUDFLARE_STREAM_DOMAIN=videodelivery.net
            </code>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoUploadExample; 